/**
 * One rule for every card that can "Match / Follow the dashboard" OR pin a range:
 *
 *   1. ScopeWindow pin  (`window` set, not "follow") → that window
 *   2. PeriodField pin  (`period` non-empty)         → that period
 *   3. Board navbar filter (when the board has one)  → that window
 *   4. Else                                          → this month
 *
 * A card's own pin wins over the navbar — only a card left on "Follow
 * dashboard" / "Match the dashboard" tracks the board's filter. Previously the
 * navbar always won even over an explicit pin, on the reasoning that a pinned
 * card sitting on a different range than the board "reads as a broken
 * filter" — reversed on Kelvin's call (2026-09-15): a component that was
 * deliberately pinned to its own range shouldn't get silently reset every
 * time the board's filter changes; other, unrelated components shouldn't be
 * "always changing because of the dashboard's setting." Follow mode is still
 * how a card tracks the board — this only changes what an explicit pin does.
 * Per-metric `sourceWindows` inside a metric plan are unaffected: those are a
 * deliberate per-source override and still beat both the pin and the board
 * for that source only.
 *
 * PeriodField ("Match the dashboard") and ScopeWindow ("Follow dashboard") both
 * mean follow — empty / "follow" are not pins.
 */

const PERIOD_ALIASES = {
  today: "today",
  Today: "today",
  yesterday: "yesterday",
  Yesterday: "yesterday",
  week: "week",
  "This Week": "week",
  "this week": "week",
  lastWeek: "lastWeek",
  "Last Week": "lastWeek",
  "last week": "lastWeek",
  lastMonth: "lastMonth",
  "Last Month": "lastMonth",
  "last month": "lastMonth",
  month: "month",
  "This Month": "month",
  "this month": "month",
  quarter: "quarter",
  "This quarter": "quarter",
  "This Quarter": "quarter",
  ytd: "ytd",
  year: "ytd",
  "This year": "ytd",
  "This Year": "ytd",
  custom: "custom",
  Custom: "custom",
};

const ALLOWED = new Set([
  "today", "yesterday", "week", "lastWeek", "lastMonth",
  "month", "quarter", "ytd", "year", "custom",
]);

/** Map PeriodField / ScopeWindow / board ids to a scoped-metrics window id. */
export const normalizeWindowId = (raw) => {
  if (raw == null || raw === "" || raw === "follow") return null;
  if (typeof raw === "number" && Number.isFinite(raw)) {
    const byIdx = {
      0: "yesterday", 1: "today", 2: "week", 3: "month", 4: "lastWeek", 5: "lastMonth",
    };
    return byIdx[raw] || null;
  }
  const mapped = PERIOD_ALIASES[raw] || String(raw);
  if (!ALLOWED.has(mapped)) return null;
  return mapped === "year" ? "ytd" : mapped;
};

/**
 * @param {object} settings  card options (window, period, customRange)
 * @param {object} board     { window|boardWindow, custom|boardCustom }
 * @returns {{ window: string, custom: object|null, follow: boolean }}
 */
export function resolveCardWindow(settings = {}, board = {}) {
  const s = settings || {};
  const boardWinRaw = board.window != null ? board.window : board.boardWindow;
  const boardCustom = board.custom != null ? board.custom : board.boardCustom;

  const pinnedWin = normalizeWindowId(s.window);
  if (pinnedWin) {
    return {
      window: pinnedWin,
      custom: pinnedWin === "custom" ? (s.customRange || null) : null,
      follow: false,
    };
  }

  const pinnedPeriod = normalizeWindowId(s.period);
  if (pinnedPeriod) {
    return {
      window: pinnedPeriod,
      custom: pinnedPeriod === "custom" ? (s.customRange || null) : null,
      follow: false,
    };
  }

  const boardWin = normalizeWindowId(boardWinRaw);
  if (boardWin) {
    return {
      window: boardWin,
      custom: boardWin === "custom" ? (boardCustom || s.customRange || null) : null,
      follow: true,
    };
  }

  return { window: "month", custom: null, follow: true };
}
